import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { AlertTriangle, Palette, Type, Layout, Music, Sparkles } from 'lucide-react';
import { VideoTemplate } from './TemplatesTab';

interface ApplyTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  template: VideoTemplate;
}

export const ApplyTemplateModal: React.FC<ApplyTemplateModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  template,
}) => {
  const settings = template.settings;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Apply Template</DialogTitle>
          <DialogDescription>
            Apply "{template.name}" to your current project?
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Template Preview */}
          <Card className="p-4">
            <div className="flex items-start gap-3">
              <div
                className="w-12 h-12 rounded flex items-center justify-center flex-shrink-0"
                style={{
                  backgroundColor: settings?.colors?.background || '#000',
                }}
              >
                <span
                  className="text-xl font-bold"
                  style={{
                    color: settings?.colors?.primary || '#fff',
                  }}
                >
                  {template.name[0]?.toUpperCase()}
                </span>
              </div>
              <div className="flex-1">
                <h4 className="font-medium">{template.name}</h4>
                {template.description && (
                  <p className="text-sm text-muted-foreground">{template.description}</p>
                )}
              </div>
            </div>
          </Card>

          {/* What will be applied */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium">This will apply:</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Palette className="h-4 w-4" />
                <span>Color palette</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Type className="h-4 w-4" />
                <span>Font settings</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Layout className="h-4 w-4" />
                <span>Layout styles</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Music className="h-4 w-4" />
                <span>Audio defaults</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Sparkles className="h-4 w-4" />
                <span>AI/Caption settings</span>
              </div>
            </div>
          </div>

          {/* Color Preview */}
          <div className="flex items-center gap-1">
            <span className="text-xs text-muted-foreground mr-2">Colors:</span>
            <div
              className="w-5 h-5 rounded"
              style={{ backgroundColor: settings?.colors?.primary || '#fff' }}
              title="Primary"
            />
            <div
              className="w-5 h-5 rounded"
              style={{ backgroundColor: settings?.colors?.secondary || '#000' }}
              title="Secondary"
            />
            <div
              className="w-5 h-5 rounded"
              style={{ backgroundColor: settings?.colors?.accent || '#3b82f6' }}
              title="Accent"
            />
            <div
              className="w-5 h-5 rounded"
              style={{ backgroundColor: settings?.colors?.background || '#000' }}
              title="Background"
            />
            <div
              className="w-5 h-5 rounded border"
              style={{ backgroundColor: settings?.colors?.text || '#fff' }}
              title="Text"
            />
          </div>

          {/* Warning */}
          <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-950/30 rounded-lg text-amber-800 dark:text-amber-200">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <p className="text-sm">
              This will update default styles but won't overwrite content you've already edited on
              the timeline.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={onConfirm}>Apply Template</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
